import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { useFormat } from '../context/FormatContext';
import { useData } from '../context/DataContext';
import { authService } from '../services/api';
import BackMatterLibrary from './BackMatterLibrary';
import ConfirmModal from './common/ConfirmModal';

const {
  FiFileText, FiPlus, FiDownload, FiCheckCircle, FiClock,
  FiTrash2, FiArrowRight, FiBookOpen, FiImage, FiSettings,
  FiLock, FiShield, FiUsers, FiAward, FiLayers, FiKey, FiAlertCircle,
  FiGrid, FiList, FiCalendar, FiLogOut, FiExternalLink
} = FiIcons;

const COVER_BASE = 'https://format.gospelwiseauthor.app/api/';

const FormatProDashboard = ({ onOpenAdmin }) => {
  const { manuscripts, startNewProject, loadProject, deleteManuscript, loading, reloadProject } = useFormat();
  const { currentUser, updateUserSession } = useData();
  const [showBackMatter, setShowBackMatter] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // View mode: 'grid' or 'row' (persisted in localStorage)
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('formatpro_manuscripts_view') || 'grid';
  });

  const handleViewChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('formatpro_manuscripts_view', mode);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('gospelwise_user');
    sessionStorage.removeItem('gospelwise_handoff_token');
    sessionStorage.removeItem('last_active_book_id');
    localStorage.removeItem('gospelwise_handoff_token');
    window.location.assign('https://gospelwiseauthor.app/#/login');
  };

  // Access Code State
  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState(null);
  const [redeemError, setRedeemError] = useState(null);

  const accessType = (currentUser?.access_type || 'trial').toLowerCase();
  const isAdmin = accessType === 'admin';
  const isLifetime = accessType === 'lifetime';
  const isSingle = accessType === 'single';
  const isTrial = accessType === 'trial';
  
  const singleAmt = Math.max(0, parseInt(currentUser?.single_amt || 0, 10));
  const projectCount = manuscripts.length;

  // Single access rules: user can create conversions up to single_amt
  const hasSingleConversionsLeft = isSingle && (projectCount < singleAmt);
  const canCreateNew = isAdmin || isLifetime || (isSingle && hasSingleConversionsLeft) || (isTrial && projectCount === 0);
  const displayName = currentUser?.name || 'Author';

  const handleRedeemCode = async (e) => {
    e.preventDefault();
    if (!accessCodeInput.trim() || redeeming) return;
    setRedeeming(true);
    setRedeemError(null);
    setRedeemSuccess(null);

    try {
      const res = await authService.redeemCode(currentUser.local_id, accessCodeInput.trim());
      if (res.success) {
        if (typeof updateUserSession === 'function') {
          updateUserSession({
            single_amt: res.single_amt,
            access_type: res.access_type
          });
        }
        setRedeemSuccess(res.message || '1 conversion unlocked successfully!');
        setAccessCodeInput('');
        setTimeout(() => setRedeemSuccess(null), 5000);
      } else {
        throw new Error(res.error || 'Failed to redeem code.');
      }
    } catch (err) {
      setRedeemError(err.message || 'Invalid access code. Please try again.');
    } finally {
      setRedeeming(false);
    }
  };

  if (showBackMatter) {
    return <BackMatterLibrary onBack={() => setShowBackMatter(false)} />;
  }

  const confirmDelete = (e, doc) => {
    e.stopPropagation();
    setDeleteTarget(doc);
  };

  const handleEdit = (e, doc) => {
    e.stopPropagation();
    reloadProject(doc);
  };

  const getCoverUrl = (doc) => {
    if (!doc.cover_path) return null;
    if (doc.cover_path.startsWith('http')) return doc.cover_path;
    return `${COVER_BASE}${doc.cover_path}`;
  };

  return (
    <div className="min-h-screen bg-cream p-6 md:p-12 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary/5 rounded-full blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto relative z-10"
      >
        {/* Welcome Banner Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-slate-200/80 pb-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-serif font-bold text-lg">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Welcome back,</p>
              <h2 className="text-xl font-serif text-slate-800 font-bold leading-tight">{displayName}</h2>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isAdmin ? (
              <div className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full text-xs font-black uppercase tracking-widest shadow-sm">
                <SafeIcon icon={FiAward} className="text-sm" />
                <span>Super Administrator</span>
              </div>
            ) : isLifetime ? (
              <div className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-xs font-bold uppercase tracking-wider">
                <SafeIcon icon={FiShield} />
                <span>Lifetime Pass (Unlimited)</span>
              </div>
            ) : isSingle ? (
              <div className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-xs font-bold uppercase tracking-wider">
                <SafeIcon icon={FiLayers} />
                <span>Single Pass ({projectCount} of {singleAmt} Used)</span>
              </div>
            ) : (
              <div className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-xs font-bold uppercase tracking-wider">
                <SafeIcon icon={FiShield} />
                <span className="capitalize">{accessType} Access</span>
              </div>
            )}

            <a
              href="https://gospelwiseauthor.app/#/dashboard"
              className="inline-flex items-center space-x-2 px-3 py-2 bg-white border border-slate-200 text-slate-600 hover:text-primary hover:border-primary/30 rounded-xl text-xs font-bold transition-all shadow-sm"
            >
              <SafeIcon icon={FiExternalLink} />
              <span>GWA App Hub</span>
            </a>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center space-x-2 px-3 py-2 bg-white border border-slate-200 text-slate-600 hover:text-red-600 hover:border-red-200 rounded-xl text-xs font-bold transition-all shadow-sm"
            >
              <SafeIcon icon={FiLogOut} />
              <span>Log Out</span>
            </button>
          </div>
        </div>

        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-xs font-bold uppercase tracking-widest">
                <SafeIcon icon={FiFileText} />
                <span>FormatterPro Workspace</span>
                <span className="mx-1 opacity-40">|</span>
                <SafeIcon icon={FiShield} />
                <span className="capitalize">{accessType} Tier</span>
              </div>

              {isAdmin && (
                <button
                  onClick={onOpenAdmin}
                  className="inline-flex items-center space-x-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-full text-xs font-black uppercase tracking-widest transition-all shadow-md shadow-purple-600/20"
                >
                  <SafeIcon icon={FiUsers} />
                  <span>Admin Portal</span>
                </button>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-serif text-primary mb-4 tracking-tight">
              Manuscript Conversions
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl font-medium">
              Professional manuscript exports for indie authors. Clean, format, and generate
              retailer-ready EPUB files and print PDFs from your Word documents.
            </p>

            {isTrial && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs flex items-center space-x-2 max-w-xl">
                <SafeIcon icon={FiLock} className="text-amber-600 flex-shrink-0" />
                <span>
                  <strong>Trial Account:</strong> You can create 1 conversion project to preview formatting results. Upgrade via Skillplate to download generated EPUB/PDF files.
                </span>
              </div>
            )}

            {isSingle && !hasSingleConversionsLeft && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-900 text-xs flex items-center space-x-2 max-w-xl">
                <SafeIcon icon={FiLayers} className="text-blue-600 flex-shrink-0" />
                <span>
                  <strong>Conversions Used:</strong> You have used all {singleAmt} of your purchased single conversion(s). Purchase another single product or upgrade to Lifetime to add more books.
                </span>
              </div>
            )}
          </div>

          {loading && (
            <div className="flex items-center space-x-3 text-primary bg-white px-6 py-3 rounded-2xl shadow-sm border border-slate-100">
              <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
              <span className="text-xs font-bold uppercase tracking-widest">Processing...</span>
            </div>
          )}
        </div>

        <div className="mb-10">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Author Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <motion.button
              whileHover={{ scale: 1.02, translateY: -3 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowBackMatter(true)}
              className="bg-white rounded-[2rem] border-2 border-slate-200 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5 transition-all p-8 text-left group"
            >
              <div className="flex items-start space-x-5">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <SafeIcon icon={FiBookOpen} className="text-2xl text-primary group-hover:text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-bold text-slate-800 text-lg">Back Matter Library</h3>
                    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 bg-primary/10 text-primary rounded-full">Global</span>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    Author your About the Author, Other Books, Newsletter Signup, and Notes once.
                  </p>
                </div>
              </div>
            </motion.button>

            {/* Access Code Card for single or trial access_type */}
            {(isSingle || isTrial) && (
              <motion.div
                whileHover={{ scale: 1.01 }}
                className="bg-white rounded-[2rem] border-2 border-blue-200/80 p-8 shadow-sm flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiKey} className="text-xl" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-lg">Access Code</h3>
                      <p className="text-xs text-slate-500">Redeem code to unlock conversions</p>
                    </div>
                  </div>

                  <form onSubmit={handleRedeemCode} className="mt-4 space-y-2">
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        placeholder="ENTER ACCESS CODE"
                        value={accessCodeInput}
                        onChange={(e) => {
                          setAccessCodeInput(e.target.value.toUpperCase());
                          setRedeemError(null);
                        }}
                        className="flex-1 px-3 py-2 text-xs font-mono font-bold tracking-wider rounded-xl border border-slate-200 focus:outline-none focus:border-blue-500 uppercase"
                      />
                      <button
                        type="submit"
                        disabled={redeeming || !accessCodeInput.trim()}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-sm"
                      >
                        {redeeming ? '...' : 'Unlock'}
                      </button>
                    </div>

                    <AnimatePresence>
                      {redeemSuccess && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 p-2 rounded-lg flex items-center space-x-1"
                        >
                          <SafeIcon icon={FiCheckCircle} className="flex-shrink-0" />
                          <span>{redeemSuccess}</span>
                        </motion.div>
                      )}
                      {redeemError && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-[11px] font-bold text-red-600 bg-red-50 border border-red-200 p-2 rounded-lg flex items-center space-x-1"
                        >
                          <SafeIcon icon={FiAlertCircle} className="flex-shrink-0" />
                          <span>{redeemError}</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </form>
                </div>

                <div className="mt-3 text-[10px] text-slate-400 font-medium">
                  {isSingle ? `Available single conversions: ${singleAmt}` : 'Unlocks 1 full conversion to your account.'}
                </div>
              </motion.div>
            )}

            {isAdmin && (
              <motion.button
                whileHover={{ scale: 1.02, translateY: -3 }}
                whileTap={{ scale: 0.98 }}
                onClick={onOpenAdmin}
                className="bg-white rounded-[2rem] border-2 border-purple-200 hover:border-purple-500 hover:shadow-xl hover:shadow-purple-500/5 transition-all p-8 text-left group ring-2 ring-purple-100"
              >
                <div className="flex items-start space-x-5">
                  <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-purple-600 group-hover:text-white transition-all duration-300">
                    <SafeIcon icon={FiUsers} className="text-2xl text-purple-600 group-hover:text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-bold text-slate-800 text-lg">Admin Management</h3>
                      <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-bold">Privileged</span>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      View authors, manage conversion counts, and assign Skillplate product tiers.
                    </p>
                  </div>
                </div>
              </motion.button>
            )}

            {!isAdmin && !isSingle && !isTrial && (
              <div className="bg-slate-50/60 rounded-[2rem] border-2 border-dashed border-slate-200 p-8 flex items-center justify-center">
                <div className="text-center text-slate-400">
                  <SafeIcon icon={FiPlus} className="text-3xl mx-auto mb-2 opacity-40" />
                  <p className="text-xs font-bold uppercase tracking-widest opacity-60">More tools coming soon</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Header row with Title and Grid / Row View Switcher */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest">
            Manuscript Conversions ({manuscripts.length})
          </h2>

          <div className="inline-flex items-center bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
            <button
              onClick={() => handleViewChange('grid')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                viewMode === 'grid'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
              title="Grid View (Large Cards)"
            >
              <SafeIcon icon={FiGrid} className="text-sm" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => handleViewChange('row')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                viewMode === 'row'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
              title="Row List View (2:3 Book Cover Thumbnails)"
            >
              <SafeIcon icon={FiList} className="text-sm" />
              <span className="hidden sm:inline">Rows</span>
            </button>
          </div>
        </div>

        {viewMode === 'grid' ? (
          /* ================= GRID VIEW ================= */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-12">
            {canCreateNew ? (
              <motion.button
                whileHover={{ scale: 1.02, translateY: -5 }}
                whileTap={{ scale: 0.98 }}
                onClick={startNewProject}
                disabled={loading}
                className="h-80 border-2 border-dashed border-slate-300 rounded-[2rem] flex flex-col items-center justify-center space-y-4 hover:border-primary hover:bg-white hover:shadow-xl hover:shadow-primary/5 transition-all group bg-white/50 disabled:opacity-50"
              >
                <div className="w-16 h-16 bg-primary/5 rounded-2xl flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <SafeIcon icon={FiPlus} className="text-3xl text-primary group-hover:text-white" />
                </div>
                <div className="text-center">
                  <span className="text-xl font-serif text-slate-800 block mb-1">New Conversion</span>
                  <span className="text-xs text-slate-500 font-medium font-sans">
                    {isSingle ? `${singleAmt - projectCount} conversion(s) remaining` : 'Upload a DOCX or DOC file'}
                  </span>
                </div>
              </motion.button>
            ) : (
              <div className="h-80 border-2 border-dashed border-amber-300 bg-amber-50/40 rounded-[2rem] flex flex-col items-center justify-center p-6 text-center">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-3">
                  <SafeIcon icon={FiLock} className="text-2xl text-amber-600" />
                </div>
                <h3 className="font-serif text-slate-800 text-lg font-bold mb-1">
                  {isSingle ? 'Single Conversions Exhausted' : 'Trial Limit Reached'}
                </h3>
                <p className="text-xs text-slate-500 mb-4 max-w-xs">
                  {isSingle
                    ? `You have created ${projectCount} book(s) out of ${singleAmt} available conversions. Purchase another single conversion or lifetime pass on Skillplate to continue.`
                    : 'Your trial includes 1 conversion project. Upgrade on Skillplate to create additional books and export without watermarks.'}
                </p>
              </div>
            )}

            {manuscripts.map(doc => {
              const coverUrl = getCoverUrl(doc);
              return (
                <motion.div
                  key={doc.id}
                  whileHover={{ scale: 1.02, translateY: -5 }}
                  className="bg-white rounded-[2rem] border border-slate-200 flex flex-col group hover:shadow-xl hover:shadow-primary/5 transition-all shadow-sm relative overflow-hidden cursor-pointer h-80"
                  onClick={() => loadProject(doc)}
                >
                  <div className="w-full h-36 rounded-t-[2rem] flex-shrink-0 overflow-hidden relative bg-slate-100 border-b border-slate-100">
                    {coverUrl ? (
                      <img
                        src={coverUrl}
                        alt={`${doc.title} cover`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
                        <SafeIcon icon={FiImage} className="text-4xl text-slate-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-t from-white/20 to-transparent pointer-events-none" />
                  </div>

                  <div className="p-6 flex flex-col flex-1 justify-between">
                    <div>
                      <div className="flex justify-between items-start mb-3">
                        <div className={`px-3 py-1 ${doc.status === 'exported' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'} text-[10px] font-black uppercase tracking-widest rounded-lg flex items-center space-x-1`}>
                          <SafeIcon icon={doc.status === 'exported' ? FiCheckCircle : FiClock} />
                          <span>{doc.status}</span>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={e => handleEdit(e, doc)}
                            className="p-2 text-slate-300 hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
                            title="Edit / Revise Manuscript"
                          >
                            <SafeIcon icon={FiSettings} className="text-lg" />
                          </button>
                          <button
                            onClick={e => confirmDelete(e, doc)}
                            className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                            title="Delete Manuscript"
                            disabled={loading}
                          >
                            <SafeIcon icon={FiTrash2} className="text-lg" />
                          </button>
                        </div>
                      </div>
                      <h3 className="text-lg font-serif text-slate-800 mb-1 truncate leading-tight group-hover:text-primary transition-colors">
                        {doc.title}
                      </h3>
                      <p className="text-xs text-slate-500 font-medium font-sans">By {doc.author}</p>
                    </div>

                    {doc.status === 'exported' ? (
                      <div className="flex items-center text-primary font-bold text-xs uppercase tracking-[0.2em] group-hover:translate-x-2 transition-transform mt-3">
                        <span>{isTrial && !isAdmin ? 'View Preview' : 'View / Re-Export'}</span>
                        <SafeIcon icon={isTrial && !isAdmin ? FiArrowRight : FiDownload} className="ml-2" />
                      </div>
                    ) : (
                      <div className="flex items-center text-slate-400 font-bold text-xs uppercase tracking-[0.2em] group-hover:text-primary transition-colors mt-3">
                        <span>Continue Conversion</span>
                        <SafeIcon icon={FiArrowRight} className="ml-2" />
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          /* ================= ROW LIST VIEW ================= */
          <div className="space-y-4 pb-12">
            {canCreateNew ? (
              <motion.button
                whileHover={{ scale: 1.005, translateY: -2 }}
                whileTap={{ scale: 0.995 }}
                onClick={startNewProject}
                disabled={loading}
                className="w-full bg-white/70 hover:bg-white border-2 border-dashed border-slate-300 hover:border-primary p-5 rounded-2xl flex items-center justify-between text-left transition-all shadow-sm hover:shadow-md group"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-16 rounded-xl border border-dashed border-primary/40 bg-primary/5 flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-all">
                    <SafeIcon icon={FiPlus} className="text-xl text-primary group-hover:text-white" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg font-bold text-slate-800 group-hover:text-primary transition-colors">
                      Start New Conversion Project
                    </h3>
                    <p className="text-xs text-slate-500">
                      {isSingle ? `${singleAmt - projectCount} conversion(s) remaining` : 'Upload a DOCX or DOC file to format as EPUB & PDF'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-primary text-xs font-bold uppercase tracking-wider pr-2">
                  <span>New Book</span>
                  <SafeIcon icon={FiArrowRight} />
                </div>
              </motion.button>
            ) : (
              <div className="w-full bg-amber-50/70 border-2 border-dashed border-amber-300 p-5 rounded-2xl flex items-center justify-between text-left">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-16 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiLock} className="text-xl text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-serif text-base font-bold text-amber-900">
                      {isSingle ? 'Single Conversions Exhausted' : 'Trial Limit Reached'}
                    </h3>
                    <p className="text-xs text-amber-700">
                      {isSingle
                        ? `All ${singleAmt} single conversions used. Purchase an access code or upgrade.`
                        : 'Your trial includes 1 conversion project. Upgrade on Skillplate for more.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {manuscripts.map(doc => {
              const coverUrl = getCoverUrl(doc);
              return (
                <motion.div
                  key={doc.id}
                  whileHover={{ scale: 1.005, translateY: -2 }}
                  onClick={() => loadProject(doc)}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-primary/40 p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm hover:shadow-lg hover:shadow-primary/5 transition-all cursor-pointer group"
                >
                  <div className="flex items-center space-x-4 md:space-x-5 min-w-0">
                    {/* Real 2:3 Aspect Ratio Book Cover Thumbnail (~1-1.5 inch height) */}
                    <div className="w-16 h-24 md:w-20 md:h-[120px] aspect-[2/3] rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 border border-slate-200/80 shadow-md relative group-hover:shadow-lg transition-shadow">
                      {coverUrl ? (
                        <img
                          src={coverUrl}
                          alt={`${doc.title} cover`}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400 p-1 text-center">
                          <SafeIcon icon={FiImage} className="text-xl mb-1 text-slate-300" />
                          <span className="text-[8px] font-bold uppercase tracking-wider text-slate-400">No Cover</span>
                        </div>
                      )}
                    </div>

                    {/* Metadata on right side */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span className={`px-2.5 py-0.5 ${doc.status === 'exported' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'} text-[10px] font-black uppercase tracking-widest rounded-md inline-flex items-center space-x-1`}>
                          <SafeIcon icon={doc.status === 'exported' ? FiCheckCircle : FiClock} className="text-xs" />
                          <span>{doc.status}</span>
                        </span>
                        {doc.publisher && (
                          <span className="text-[11px] font-medium text-slate-400 border border-slate-200 px-2 py-0.2 rounded-md">
                            {doc.publisher}
                          </span>
                        )}
                        {doc.language && (
                          <span className="text-[10px] font-bold uppercase text-slate-400 bg-slate-100 px-1.5 py-0.2 rounded">
                            {doc.language}
                          </span>
                        )}
                      </div>

                      <h3 className="text-base md:text-lg font-serif font-bold text-slate-800 truncate group-hover:text-primary transition-colors">
                        {doc.title}
                      </h3>
                      <p className="text-xs text-slate-500 font-medium">
                        By <span className="text-slate-700">{doc.author || 'Unknown Author'}</span>
                      </p>

                      {doc.created_at && (
                        <div className="flex items-center space-x-1 text-[11px] text-slate-400 mt-2">
                          <SafeIcon icon={FiCalendar} className="text-xs" />
                          <span>Added {new Date(doc.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions Right Side */}
                  <div className="flex items-center justify-between sm:justify-end space-x-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={e => handleEdit(e, doc)}
                        className="p-2.5 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
                        title="Edit / Revise Manuscript"
                      >
                        <SafeIcon icon={FiSettings} className="text-base" />
                      </button>
                      <button
                        onClick={e => confirmDelete(e, doc)}
                        className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                        title="Delete Manuscript"
                        disabled={loading}
                      >
                        <SafeIcon icon={FiTrash2} className="text-base" />
                      </button>
                    </div>

                    <div className="flex items-center text-primary font-bold text-xs uppercase tracking-wider pl-2 group-hover:translate-x-1 transition-transform">
                      <span>{doc.status === 'exported' ? (isTrial && !isAdmin ? 'Preview' : 'Export') : 'Open'}</span>
                      <SafeIcon icon={doc.status === 'exported' ? (isTrial && !isAdmin ? FiArrowRight : FiDownload) : FiArrowRight} className="ml-1.5" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteManuscript(deleteTarget.id)}
        variant="danger"
        title="Delete Manuscript?"
        message={`This will permanently delete "${deleteTarget?.title}", its original Word document, and all exported EPUB files.`}
        confirmText="Yes, Delete All"
      />
    </div>
  );
};

export default FormatProDashboard;